import React, { useState, useContext } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions,
  Button,
  Chip,
  Stack,
  IconButton,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';
import projects from '../data/project-data';
import AuthContext from "../store/AuthContext";

// Extract unique tags from projects for filtering
const allTags = ['All', 'Startups', ...new Set(projects.flatMap(project => project.tags).filter(tag => tag !== 'Startups'))];

const ProjectsSection = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [activeFilter, setActiveFilter] = useState('Startups');
  const theme = useTheme();

  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(project => project.tags.includes(activeFilter));

  const handleFilterChange = (event, newFilter) => {
    setActiveFilter(newFilter);
  };

  return (
    <Box 
      sx={{ 
        py: { xs: 8, md: 12 },
        backgroundColor: 'rgba(246, 248, 255, 0.8)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div"
              color="primary"
              fontWeight={600}
              sx={{ mb: 1, letterSpacing: 2 }}
            >
              OUR WORK
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Featured Projects
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                fontSize: '1.1rem'
              }}
            >
              Solutions we've built for small businesses and local small business owners across different industries.
            </Typography>
          </Box>
        </motion.div>

        {/* Filter Tabs */}
        <Box sx={{ width: '100%', mb: 4, display: 'flex', justifyContent: 'center' }}>
          <Tabs 
            value={activeFilter} 
            onChange={handleFilterChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ 
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
              },
              '& .MuiTab-root': {
                fontWeight: 500,
                textTransform: 'none',
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                }
              }
            }}
          >
            {allTags.map((tag) => (
              <Tab key={tag} label={tag} value={tag} />
            ))}
          </Tabs>
        </Box>
        
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {filteredProjects.length === 0 ? (
                <Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
                  <Typography variant="h6">No projects found with this filter.</Typography>
                </Box>
              ) : (
                filteredProjects.map((project, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true, margin: "-50px" }}
                    >
                      <Card 
                        elevation={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          border: '1px solid rgba(230, 230, 230, 0.5)',
                          transition: 'all 0.3s ease',
                          backgroundColor: 'white',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
                          }
                        }}
                      >
                        {project.screenshot && (
                          <CardMedia
                            component="img"
                            height="180"
                            image={project.screenshot}
                            alt={project.name}
                            sx={{ 
                              objectFit: 'cover',
                              width: '100%',
                              aspectRatio: '16/9'
                            }}
                          />
                        )}
                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          <Typography 
                            gutterBottom 
                            variant="h6" 
                            component="h3" 
                            fontWeight={600}
                          >
                            {project.name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ mb: 2 }}
                          >
                            {project.description}
                          </Typography>
                          <Stack 
                            direction="row" 
                            spacing={1}
                            flexWrap="wrap"
                            gap={1}
                          >
                            {project.tags.slice(0, 3).map((tag, tagIndex) => (
                              <Chip 
                                key={tagIndex} 
                                label={tag} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: `${theme.palette.primary.main}15`,
                                  color: theme.palette.primary.main,
                                  fontWeight: 500
                                }} 
                              />
                            ))}
                            {project.tags.length > 3 && (
                              <Chip 
                                label={`+${project.tags.length - 3}`} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: `${theme.palette.secondary.main}15`,
                                  color: theme.palette.secondary.main,
                                  fontWeight: 500
                                }} 
                              />
                            )}
                          </Stack>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0 }}>
                          {project.liveUrl && (
                            <Button 
                              size="small" 
                              href={project.liveUrl} 
                              target="_blank"
                              rel="noopener"
                              startIcon={<LaunchIcon />}
                              sx={{ mr: 1 }}
                            >
                              Live Demo
                            </Button>
                          )}
                          {project.repoLink && (
                            <Button 
                              size="small" 
                              href={project.repoLink} 
                              target="_blank"
                              rel="noopener"
                              startIcon={<GitHubIcon />}
                            >
                              Code
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </motion.div>
                  </Grid>
                ))
              )}
            </Grid>
          </motion.div>
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default ProjectsSection; 